import {
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Tooltip,
	Typography,
} from '@mui/material';

import { useEffect, useState } from 'react';

import { ReactComponent as ExpandMoreRoundedIcon } from 'common/assets/images/select-chevron.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import SearchSelectDropdownEle, {
	ISelectOption,
} from 'common/components/SearchableSelectDropdown/SearchSelectDropdown';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { getListOfUsers } from 'features/HorizonScanning/api';
import {
	IAssignedUserDropdownProps,
	IUser,
} from 'features/HorizonScanning/api/type-def';

import styles from './AssignUserDropdown.module.scss';

const AssignUserDropdown = (props: IAssignedUserDropdownProps) => {
	const { OK } = HTTP_STATUS_CODES;
	const { displayValue, saveButtonHandler, rowData } = props;
	const {
		backgroundColor,
		borderColor,
		border,
		selectHeight,
		selectWidth,
		paddingDisplay,
		selectDisplayHeight,
	} = styles;

	/* Local states */
	const [open, setOpen] = useState<boolean>(false);
	const [selectedValue, setSelectedValue] = useState<any>();
	const [isCanceled, setIsCanceled] = useState<boolean>(true);
	const [usersList, setUsersList] = useState<any>();
	const [searchText, setSearchText] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(true);

	/* Local Variables */
	const userOptions = usersList?.map((user: IUser) => ({
		id: user.id,
		name: user.fullName,
	}));
	const selectStyles = {
		backgroundColor,
		border: `${border} solid`,
		borderColor,
		width: selectWidth,
		height: selectHeight,
	};

	/**
	 * @description method to open the menu item
	 */
	const handleOpen = () => {
		setOpen(true);
	};

	/**
	 * @description method to close the menu item
	 */
	const handleClose = () => {
		setOpen((prev) => !prev);
	};

	/**
	 * @description method to handle change in search dropdown value
	 */
	const handleChange = (event: any) => {
		const value = event.target.value as string;
		const selectedObj = userOptions?.find(
			(ele: ISelectOption) => ele?.name === value,
		);
		if (selectedObj) {
			setSelectedValue(selectedObj);
		}
	};

	/**
	 * @description method to fetch list of users from api
	 */
	const fetchListOfUsers = async (searchUsers: string) => {
		const res = await getListOfUsers(searchUsers);
		setIsLoading(false);
		if (res?.statusCode === OK) {
			const response = res?.responseBody;
			setUsersList(response);
		}
	};

	/* fetch the updated list of user based on change in search text */
	useEffect(() => {
		if (open || !isCanceled) {
			fetchListOfUsers(searchText);
		}
	}, [searchText, open, isCanceled]);

	return (
		<FormControl variant="filled" data-testid="form-control">
			<InputLabel id="test-select-label" className={styles['label-style']} />
			<Select
				open={open || !isCanceled}
				onOpen={handleOpen}
				onClick={handleClose}
				labelId="filled-select-adornment"
				IconComponent={ExpandMoreRoundedIcon}
				label={toSentenceCase('Assign user')}
				data-testid="select-user"
				renderValue={() => (
					<Tooltip title={displayValue || ''} placement="top" arrow>
						<Typography
							className={styles['text-section']}
							data-testid="display-value"
						>
							{displayValue && displayValue.length > 18
								? `${displayValue.substring(0, 18)}...`
								: displayValue || ''}
						</Typography>
					</Tooltip>
				)}
				sx={selectStyles}
				SelectDisplayProps={{
					style: { padding: paddingDisplay, minHeight: selectDisplayHeight },
				}}
				variant="filled"
				inputProps={{
					'data-testid': 'select-id',
					'aria-label': 'Without label',
					style: { paddingBottom: '0' },
				}}
				displayEmpty
				disableUnderline
				MenuProps={{
					anchorOrigin: {
						vertical: 'bottom',
						horizontal: 'left',
					},
					transformOrigin: {
						vertical: 'top',
						horizontal: 'left',
					},
					sx: {
						'&& .Mui-selected': {
							backgroundColor,
						},
					},
				}}
			>
				{isLoading ? (
					<Loader show className={styles['loader-styling']} />
				) : (
					<MenuItem
						onClick={handleClose}
						className={styles['list-items']}
						data-testid="user-item"
					>
						<Grid item className={styles['grid-wrapper']}>
							<SearchSelectDropdownEle
								value={
									selectedValue?.name?.length > 0
										? selectedValue.name
										: displayValue
								}
								onChange={(event) => {
									handleChange(event);
								}}
								onInputChange={(e: any) => {
									setSearchText(e?.target?.value);
								}}
								data-testid="search-dropdown"
								options={userOptions}
								displayEmpty
								placeholder=""
								textValue={searchText}
								showMenuItemsTop={false}
								inputProps={{
									'data-testid': 'input-text-search',
								}}
								label={toSentenceCase('Assign user')}
								stylesClassName={styles['width-class']}
							/>
						</Grid>
					</MenuItem>
				)}
				<div className={styles['footer-wrapper']}>
					<Grid container className={styles['button-container']}>
						<Grid item className={styles['button-style']}>
							<ButtonComp
								btnType="secondary"
								clickHandler={() => {
									setIsCanceled(true);
								}}
								data-testid="cancel-btn"
							>
								Cancel
							</ButtonComp>
							<ButtonComp
								btnType="primary"
								disabled={!selectedValue?.name?.length}
								clickHandler={() => {
									saveButtonHandler(selectedValue, rowData, usersList);
								}}
								compClass={styles['save-btn']}
								data-testid="save-btn"
							>
								Save
							</ButtonComp>
						</Grid>
					</Grid>
				</div>
			</Select>
		</FormControl>
	);
};

export default AssignUserDropdown;
